import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Award, Users, Target } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const valueIcons = [Shield, Award, Users, Target];

const AboutUs = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t } = useLanguage();

  const values = [
    { icon: valueIcons[0], title: t('about.values.v1.title'), description: t('about.values.v1.desc') },
    { icon: valueIcons[1], title: t('about.values.v2.title'), description: t('about.values.v2.desc') },
    { icon: valueIcons[2], title: t('about.values.v3.title'), description: t('about.values.v3.desc') },
    { icon: valueIcons[3], title: t('about.values.v4.title'), description: t('about.values.v4.desc') },
  ];

  return (
    <section id="about" ref={ref} className="py-32 bg-[#1A1A1A] relative overflow-hidden">
      {/* Decorative top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      {/* Decorative gold corners */}
      <div className="absolute top-12 left-12 w-24 h-24 border-l-2 border-t-2 border-[#C5A55C]/40" />
      <div className="absolute bottom-12 right-12 w-24 h-24 border-r-2 border-b-2 border-[#C5A55C]/40" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Left Column - Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <motion.span 
              className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {t('about.tagline')}
            </motion.span>
            
            <h2 className="font-montserrat font-light text-4xl md:text-5xl text-white tracking-[0.08em] uppercase mb-6 leading-tight">
              {t('about.title1')}
              <br />
              <span className="text-[#C5A55C]">{t('about.title2')}</span>
            </h2>
            
            <div className="w-16 h-[2px] bg-[#C5A55C] mb-10" />
            
            <div className="space-y-6">
              <p className="font-raleway font-normal text-base text-white/80 leading-relaxed tracking-wide">
                <span className="text-[#C5A55C] font-medium">Martinez Solutions</span> {t('about.description1')}
              </p>
              
              <p className="font-raleway font-normal text-base text-white/60 leading-relaxed tracking-wide">
                {t('about.description2')}
              </p>
              
              <p className="font-raleway font-normal text-base text-white/60 leading-relaxed tracking-wide">
                {t('about.description3')}
              </p>
            </div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-12 px-10 py-4 bg-[#C5A55C] text-white hover:bg-white hover:text-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase shadow-lg hover:shadow-xl"
            >
              {t('about.cta')}
            </motion.button>
          </motion.div>

          {/* Right Column - Values */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {values.map((value, index) => (
              <ValueCard
                key={index}
                value={value}
                inView={inView}
                delay={0.3 + index * 0.1}
                index={index}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ValueCard = ({ value, inView, delay, index }) => {
  const Icon = value.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.8, ease: 'easeOut' }}
      className="group p-8 bg-[#252525] hover:bg-[#2A2A2A] border border-[#C5A55C]/20 hover:border-[#C5A55C]/50 transition-all duration-500 relative overflow-hidden"
    >
      {/* Shimmer effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#C5A55C]/15 to-transparent" />
      </div>
      
      <div className="w-12 h-12 bg-[#C5A55C]/20 group-hover:bg-[#C5A55C] flex items-center justify-center transition-all duration-300 mb-6 relative z-10">
        <Icon className="w-5 h-5 text-[#C5A55C] group-hover:text-white stroke-[1.5] transition-colors duration-300" />
      </div>
      
      <h3 className="font-montserrat font-medium text-base text-white tracking-wide mb-3 relative z-10">
        {value.title}
      </h3>
      <p className="font-raleway font-normal text-sm text-white/60 leading-relaxed relative z-10">
        {value.description}
      </p>
      
      {/* Gold accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5A55C] scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
    </motion.div>
  );
};

export default AboutUs;
